import {
  CATALOGO_TECNICAS_CORTE,
  type IdTecnicaCorte,
} from '../dominio/didatico/tecnicasCorteCatalogo'
import {
  CONFIGURACOES,
  portaProntaParaRemocao,
  vinculosRestantes,
  type ConfiguracaoOficinaCorte,
  type EstadoPortaOficina,
  type FerramentaOficinaCorte,
} from '../dominio/didatico/oficinaCorte'
import type { PlayerOficinaCorte } from './oficina/usePlayerOficinaCorte'
import {
  IconeConcluido,
  IconeInfo,
  IconeInteriorExtracao,
  IconeLagrima,
  IconePausar,
  IconeReiniciar,
  IconeReproduzir,
  IconeRetirarPeca,
  IconeRisco,
  IconeSustentarPeca,
  IconeVidro,
} from './icones'
import {
  ATALHO_MODO_ALARGADOR,
  FERRAMENTAS_DE_INTERVENCAO,
  MODOS_DO_ALARGADOR,
} from './oficina/ferramentasOficina'
import type { ModoAlargador } from '../dominio/didatico/interacaoOficina'

export type { ConfiguracaoOficinaCorte, FerramentaOficinaCorte } from '../dominio/didatico/oficinaCorte'
export type SecaoPainelOficinaCorte = 'cenario' | 'ferramentas' | 'vidros' | 'reforcos' | 'tecnica'
export type PecaOficinaCorte = 'porta' | 'tampaTraseira' | 'terceiraPorta'
export type ModoOficinaCorte = 'demonstracao' | 'pratica'

interface Props {
  secoes?: readonly SecaoPainelOficinaCorte[]
  player: PlayerOficinaCorte
  aoSelecionarTecnica?: () => void
  configuracao: ConfiguracaoOficinaCorte
  pinturaPorAco: boolean
  aoEscolherConfiguracao: (valor: ConfiguracaoOficinaCorte) => void
  aoAlternarReforcos: (valor: boolean) => void
  aoReenquadrarCamera?: () => void
  modo?: ModoOficinaCorte
  aoMudarModo?: (modo: ModoOficinaCorte) => void
  ferramenta?: FerramentaOficinaCorte
  aoEscolherFerramenta?: (ferramenta: FerramentaOficinaCorte) => void
  modoAlargador?: ModoAlargador
  aoEscolherModoAlargador?: (modo: ModoAlargador) => void
  estadoPorta?: EstadoPortaOficina
  mensagemInteracao?: string
  segurandoFerramenta?: boolean
  progressoFerramenta?: number
  progressoEstrutural?: number
  aoManejarVidro?: () => void
  aoTestarManual?: () => void
  aoSustentarPorta?: () => void
  aoRemoverPorta?: () => void
  aoReiniciarPratica?: () => void
}

function statusTexto(player: PlayerOficinaCorte): string {
  if (player.estadoAnimacao === 'concluido') return 'Demonstração concluída.'
  if (player.estadoAnimacao === 'erro') return player.erro ?? 'A demonstração foi interrompida.'
  if (player.estadoAnimacao === 'pausado') return 'Demonstração pausada.'
  if (player.estadoAnimacao === 'enquadrando') return 'Enquadrando a próxima ação.'
  if (player.estadoAnimacao === 'aproximando') return 'Posicionando a ferramenta.'
  if (player.estadoAnimacao === 'contato') return 'Estabelecendo contato com a peça.'
  if (player.estadoAnimacao === 'executando') return 'Executando a ação da etapa.'
  if (player.estadoAnimacao === 'recuando') return 'Afastando a ferramenta.'
  return 'Mostrando o resultado da ação.'
}

/** Painel de acompanhamento: demonstração guiada ou prática direta de oficina. */
export function PainelOficinaCorte({
  secoes,
  player,
  aoSelecionarTecnica,
  configuracao,
  pinturaPorAco,
  aoEscolherConfiguracao,
  aoAlternarReforcos,
  aoReenquadrarCamera,
  modo = 'demonstracao',
  aoMudarModo,
  ferramenta = 'cortadorHidraulico',
  aoEscolherFerramenta,
  modoAlargador = 'abrir',
  aoEscolherModoAlargador,
  estadoPorta,
  mensagemInteracao,
  segurandoFerramenta = false,
  progressoFerramenta = 0,
  aoManejarVidro,
  aoTestarManual,
  aoSustentarPorta,
  aoRemoverPorta,
  aoReiniciarPratica,
}: Props) {
  const secoesExibir = new Set(secoes ?? ['tecnica', 'reforcos', 'cenario'])
  const { tecnica, etapaAtual, indiceEtapa, etapasConcluidas } = player
  const ferramentaAtual = FERRAMENTAS_DE_INTERVENCAO.find((f) => f.id === etapaAtual.ferramenta)

  return (
    <div className="player-corte-container">
      {/* Seletor de modo oculto: por ora só a demonstração guiada. A prática
          livre continua abaixo para quem a ativar por `modo`, mas sem entrada
          na interface até o posicionamento das ferramentas melhorar. */}
      {modo === 'pratica' && (
        <div className="grupo-controle">
          <button
            type="button"
            className="btn-acao-cad btn-acao-cad--secundario"
            onClick={() => aoMudarModo?.('demonstracao')}
          >
            Voltar à demonstração guiada
          </button>
        </div>
      )}

      {modo === 'demonstracao' ? (
        secoesExibir.has('tecnica') && (
          <>
            <div className="grupo-controle">
              <span className="titulo-controle">Demonstração de técnica de corte</span>
              <label className="campo">
                <span>técnica</span>
                <select value={tecnica.id} onChange={(e) => {
                  player.selecionarTecnica(e.target.value as IdTecnicaCorte)
                  aoSelecionarTecnica?.()
                }}>
                  {CATALOGO_TECNICAS_CORTE.map((item) => <option key={item.id} value={item.id}>{item.nome}</option>)}
                </select>
              </label>
              <p className="descricao-fase">{tecnica.descricao}</p>
            </div>

            <div className="barra-transporte-corte">
              <div className="status-reproducao-corte">
                <span>{statusTexto(player)}</span>
                <strong>{indiceEtapa + 1} / {tecnica.etapas.length}</strong>
              </div>
              <div className="barra-progresso" style={{ height: '6px' }}>
                <i style={{ width: `${((indiceEtapa + (player.reproduzindo ? player.progressoFase : 0)) / tecnica.etapas.length) * 100}%` }} />
              </div>
              <div className="controles-transporte-botoes">
                <button type="button" className="btn-acao-cad btn-acao-cad--primario" onClick={player.alternarReproducao}>
                  {player.reproduzindo ? <><IconePausar tamanho={16} /> Pausar</> : <><IconeReproduzir tamanho={16} /> {player.estadoAnimacao === 'concluido' ? 'Assistir novamente' : player.estadoAnimacao === 'erro' ? 'Recomeçar' : 'Continuar'}</>}
                </button>
                <button type="button" className="btn-acao-cad btn-acao-cad--secundario" onClick={player.reiniciar} aria-label="Recomeçar demonstração" title="Recomeçar demonstração">
                  <IconeReiniciar tamanho={16} /> Recomeçar
                </button>
                <button type="button" className="btn-acao-cad btn-acao-cad--secundario" onClick={player.alternarCameraGuiada}>
                  Câmera guiada: {player.cameraGuiada ? 'ativada' : 'manual'}
                </button>
                {aoReenquadrarCamera && (
                  <button type="button" className="btn-acao-cad btn-acao-cad--secundario" onClick={() => { player.focarZonaAtual(); aoReenquadrarCamera() }}>
                    Focar
                  </button>
                )}
              </div>
            </div>

            <div className="card-etapa-didatica">
              <h4><span style={{ color: 'var(--color-fill-primary)' }}>Etapa {indiceEtapa + 1}:</span> {etapaAtual.titulo}</h4>
              <p className="etapa-ferramenta">
                {ferramentaAtual ? ferramentaAtual.nome : 'Ação coordenada da equipe'}
              </p>
              <p>{etapaAtual.explicacao}</p>
              <div className="alerta-risco-corte"><strong><IconeRisco tamanho={13} /> Riscos:</strong> {etapaAtual.riscos}</div>
              <div className="dica-seguranca-corte"><strong><IconeInfo tamanho={13} /> Segurança:</strong> {etapaAtual.dicaSeguranca}</div>
            </div>

            <div className="grupo-controle">
              <span className="titulo-controle">Roteiro da demonstração</span>
              <ul className="checklist-etapas-corte">
                {tecnica.etapas.map((etapa, indice) => {
                  const concluida = etapasConcluidas.includes(etapa.id)
                  return (
                    <li key={etapa.id} className={`item-etapa-corte ${indice === indiceEtapa ? 'atual' : ''} ${concluida ? 'concluida' : ''}`}>
                      <span className={`icone-status-etapa ${concluida ? 'concluida' : indice === indiceEtapa ? 'atual' : 'pendente'}`}>
                        {concluida ? <IconeConcluido tamanho={14} /> : indice + 1}
                      </span>
                      <div><strong>{etapa.titulo}</strong></div>
                    </li>
                  )
                })}
              </ul>
              {/* Vale para toda etapa com ferramenta: dito uma vez com destaque POP */}
              <div className="selo-pop-lagrima" role="note" aria-label="Procedimento Operacional Padrão de Proteção Rígida">
                <span className="selo-pop-lagrima__icone">
                  <IconeLagrima tamanho={15} />
                </span>
                <div className="selo-pop-lagrima__conteudo">
                  <span className="selo-pop-lagrima__cabecalho">POP • Proteção Rígida (Lágrima)</span>
                  <p className="selo-pop-lagrima__texto">
                    Obrigatória: deve ser mantida sempre interposta entre a vítima e a ferramenta/chapa em operação.
                  </p>
                </div>
              </div>
            </div>
          </>
        )
      ) : (
        /* Modo Prática Livre */
        <>
          <div className="card-etapa-didatica">
            <h4>
              <span style={{ color: 'var(--color-fill-primary)' }}>Prática:</span> Retirada da Porta Dianteira
            </h4>
            <p style={{ margin: '4px 0 8px 0', fontSize: 'var(--text-sm)', color: 'var(--color-text-primary)' }}>
              {mensagemInteracao ?? 'Selecione uma ferramenta e pressione ou segure um alvo na porta 3D.'}
            </p>
            {segurandoFerramenta && (
              <div style={{ marginTop: '8px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 'var(--text-xs)', marginBottom: '4px' }}>
                  <span>Acionando ferramenta sob pressão...</span>
                  <strong>{Math.round(progressoFerramenta * 100)}%</strong>
                </div>
                <div className="barra-progresso" style={{ height: '8px' }}>
                  <i style={{ width: `${progressoFerramenta * 100}%`, background: 'var(--color-fill-primary)' }} />
                </div>
              </div>
            )}
          </div>

          <div className="grupo-controle">
            <span className="titulo-controle">Equipar ferramenta de intervenção</span>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))', gap: '6px' }}>
              {FERRAMENTAS_DE_INTERVENCAO.map((f) => {
                const equipada = ferramenta === f.id
                return (
                  <button
                    key={f.id}
                    type="button"
                    className={`btn-acao-cad btn-ferramenta-oficina ${equipada ? 'btn-acao-cad--primario' : 'btn-acao-cad--secundario'}`}
                    onClick={() => aoEscolherFerramenta?.(f.id)}
                    title={`${f.nome} (${f.atalho}) — ${f.dica}`}
                    style={{ justifyContent: 'flex-start', padding: '6px 10px', height: 'auto', gap: '6px' }}
                  >
                    <f.Icone tamanho={16} />
                    <span style={{ fontSize: '0.8rem' }}>{f.nomeCurto}</span>
                    <small style={{ marginLeft: 'auto', opacity: 0.6, fontSize: '0.7rem' }}>[{f.atalho}]</small>
                  </button>
                )
              })}
            </div>
            {ferramenta === 'alargador' && (
              <div style={{ marginTop: '8px' }}>
                <span className="titulo-controle" style={{ fontSize: '0.75rem', marginBottom: '4px' }}>
                  Modo do alargador ({ATALHO_MODO_ALARGADOR}):
                </span>
                <div style={{ display: 'flex', gap: '6px' }}>
                  {MODOS_DO_ALARGADOR.map((m) => (
                    <button
                      key={m.id}
                      type="button"
                      className={`btn-acao-cad ${modoAlargador === m.id ? 'btn-acao-cad--primario' : 'btn-acao-cad--secundario'}`}
                      onClick={() => aoEscolherModoAlargador?.(m.id)}
                      style={{ flex: 1, padding: '4px 8px', fontSize: '0.78rem' }}
                    >
                      <m.Icone tamanho={14} /> {m.nome}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {estadoPorta && (
            <div className="grupo-controle">
              <span className="titulo-controle">
                Vínculos estruturais da porta ({vinculosRestantes(estadoPorta).length} restantes)
              </span>
              <ul className="checklist-etapas-corte">
                {[
                  { id: 'dobradicaSuperior', nome: 'Dobradiça superior' },
                  { id: 'dobradicaInferior', nome: 'Dobradiça inferior' },
                  { id: 'limitador', nome: 'Limitador de abertura' },
                  { id: 'trava', nome: 'Pino da trava' },
                  { id: 'fiacao', nome: 'Fiação elétrica' },
                ].map((v) => {
                  const liberado = estadoPorta.vinculos[v.id as keyof typeof estadoPorta.vinculos] === 'liberado'
                  return (
                    <li
                      key={v.id}
                      className={`item-etapa-corte ${liberado ? 'concluida' : ''}`}
                      style={{ cursor: 'default' }}
                    >
                      <span className={`icone-status-etapa ${liberado ? 'concluida' : 'pendente'}`}>
                        {liberado ? <IconeConcluido tamanho={14} /> : '•'}
                      </span>
                      <div style={{ flex: 1 }}>
                        <strong>{v.nome}</strong>: {liberado ? 'Liberado' : 'Íntegro'}
                      </div>
                    </li>
                  )
                })}
                <li
                  className={`item-etapa-corte ${estadoPorta.vidro !== 'intacto' ? 'concluida' : ''}`}
                  style={{ cursor: 'default' }}
                >
                  <span className={`icone-status-etapa ${estadoPorta.vidro !== 'intacto' ? 'concluida' : 'pendente'}`}>
                    {estadoPorta.vidro !== 'intacto' ? <IconeConcluido tamanho={14} /> : '!'}
                  </span>
                  <div style={{ flex: 1 }}>
                    <strong>Vidro da janela</strong>: {estadoPorta.vidro === 'intacto' ? 'Intacto (risco de ruptura sob intervenção!)' : estadoPorta.vidro}
                  </div>
                </li>
              </ul>
            </div>
          )}

          <div className="grupo-controle">
            <span className="titulo-controle">Ações da equipe e remoção</span>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px' }}>
              <button
                type="button"
                className="btn-acao-cad btn-acao-cad--secundario"
                onClick={aoManejarVidro}
                disabled={estadoPorta?.vidro !== 'intacto'}
                title="Gerenciar vidro antes da intervenção para evitar estilhaços"
              >
                <IconeVidro tamanho={15} /> Manejar vidro
              </button>
              <button
                type="button"
                className="btn-acao-cad btn-acao-cad--secundario"
                onClick={aoTestarManual}
                disabled={estadoPorta?.removida}
                title="Verificar se a porta já abre manualmente"
              >
                <IconeInteriorExtracao tamanho={15} /> Testar abertura
              </button>
              <button
                type="button"
                className={`btn-acao-cad ${estadoPorta?.sustentada ? 'btn-acao-cad--primario' : 'btn-acao-cad--secundario'}`}
                onClick={aoSustentarPorta}
                disabled={estadoPorta?.removida}
                title="Equipe sustenta a porta antes da liberação final"
              >
                <IconeSustentarPeca tamanho={15} /> {estadoPorta?.sustentada ? 'Porta sustentada' : 'Sustentar porta'}
              </button>
              <button
                type="button"
                className={`btn-acao-cad ${estadoPorta && portaProntaParaRemocao(estadoPorta) ? 'btn-acao-cad--primario' : 'btn-acao-cad--secundario'}`}
                onClick={aoRemoverPorta}
                disabled={estadoPorta?.removida}
                title="Remover porta após liberar todos os vínculos e sustentar"
              >
                <IconeRetirarPeca tamanho={15} /> {estadoPorta?.removida ? 'Porta removida' : 'Retirar porta'}
              </button>
            </div>
            <div style={{ marginTop: '8px' }}>
              <button
                type="button"
                className="btn-acao-cad btn-acao-cad--secundario"
                onClick={aoReiniciarPratica}
                style={{ width: '100%' }}
              >
                <IconeReiniciar tamanho={14} /> Reiniciar exercício de corte
              </button>
            </div>
          </div>
        </>
      )}

      {secoesExibir.has('cenario') && (
        <div className="grupo-controle">
          <span className="titulo-controle">Posição do veículo</span>
          <div className="trilha-cenarios" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '6px' }}>
            {CONFIGURACOES.map((item) => (
              <button
                key={item.id}
                type="button"
                className={`btn-acao-cad ${configuracao === item.id ? 'btn-acao-cad--primario' : 'btn-acao-cad--secundario'}`}
                onClick={() => aoEscolherConfiguracao(item.id)}
                title={item.descricao}
              >
                {item.nome}
              </button>
            ))}
          </div>
        </div>
      )}

      {secoesExibir.has('reforcos') && (
        <div className="grupo-controle">
          <span className="titulo-controle">Detalhes visuais</span>
          <label className="toggle toggle-instabilidade">
            <input type="checkbox" checked={pinturaPorAco} onChange={(event) => aoAlternarReforcos(event.target.checked)} />
            destacar reforços e classes de aço
          </label>
        </div>
      )}
    </div>
  )
}
